import cn from 'classnames';
import { AnimatePresence, motion, PanInfo } from 'framer-motion';
import { observer } from 'mobx-react-lite';
import * as React from 'react';

import { useRootStore, useUiStore } from '@/configs/store';

import Alert from './Alert/Alert';

import s from './Alerts.module.scss';

type Props = {
  className?: string;
};

const INITIAL_ANIMATION = {
  opacity: 0,
  height: 0,
  marginBlock: 0,
};

const ANIMATION_DESKTOP = {
  opacity: 1,
  height: 'auto',
  marginBlock: '0 20px',
};

const ANIMATION_MOBILE = {
  opacity: 1,
  height: 'auto',
  marginBlock: '10px 0',
};

const SWIPE_THRESHOLD = 100;
const SWIPE_VELOCITY_THRESHOLD = 500;

const Alerts = observer(({ className }: Props): React.ReactElement<Props> => {
  const {
    alertsStore: { alerts, removeAlert },
  } = useRootStore();

  const { isTabletAndLower } = useUiStore();

  const handleDragEnd = React.useCallback(
    (alertId: string) => (_: unknown, info: PanInfo) => {
      const swipeDistance = info.offset.x;
      const swipeVelocity = info.velocity.x;

      if (
        (swipeDistance > SWIPE_THRESHOLD && swipeVelocity > 0) ||
        swipeVelocity > SWIPE_VELOCITY_THRESHOLD
      ) {
        removeAlert(alertId);
      }
    },
    [removeAlert],
  );

  return (
    <ul className={cn(s.alerts, className, { [s.alerts_mobile]: isTabletAndLower })}>
      <AnimatePresence>
        {alerts.items.map((alert) => (
          <motion.li
            key={alert.id}
            initial={INITIAL_ANIMATION}
            animate={isTabletAndLower ? ANIMATION_MOBILE : ANIMATION_DESKTOP}
            exit={INITIAL_ANIMATION}
            drag={isTabletAndLower ? 'x' : false}
            dragConstraints={{ left: 0, right: 300 }}
            dragElastic={0.2}
            onDragEnd={handleDragEnd(alert.id)}
            whileDrag={isTabletAndLower ? { scale: 0.95 } : undefined}
          >
            <Alert {...alert} onRemove={removeAlert} />
          </motion.li>
        ))}
      </AnimatePresence>
    </ul>
  );
});

export default Alerts;
